import React, { forwardRef, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import {
	aiAnswerCollapseClicked,
	aiAnswerExpandClicked,
} from '../../../ai-answer-dialog/analytics';
import { useAnalytics } from '../../../common';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../../common/controller/analytics/hooks/use-sain-usage';
import { QueryIntent } from '../../../constants';
import { ShowMoreButton } from '../ui/show-more-button';

import { messages } from './messages';

const styles = cssMap({
	collapsableContainer: {
		overflow: 'hidden',
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.100'),
	},
	header: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.150'),
	},
	pointer: {
		cursor: 'pointer',
	},
	rootCollapsed: {
		height: '120px',
		maskImage: 'linear-gradient(180deg, #000 65%, transparent)',
	},
	rootCollapsedWithReadMore: {
		height: '92px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maskImage: 'linear-gradient(180deg, #000 65%, transparent)',
	},
	rootExpanded: {
		// @ts-expect-error - TS2322 - workaround to fix TabPanel's overly-weighted specificity for minHeight styling
		minHeight: '120px !important', // eslint-disable-line @atlaskit/ui-styling-standard/no-important-styles
	},
	explicitQuestionCollapsed: {
		height: '270px',
		maskImage: 'linear-gradient(180deg, #000 65%, transparent)',
	},
	explicitQuestionCollapsedWithReadMore: {
		height: '242px',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maskImage: 'linear-gradient(180deg, #000 65%, transparent)',
	},
	explicitQuestionExpanded: {
		// @ts-expect-error - TS2322 - workaround to fix TabPanel's overly-weighted specificity for minHeight styling
		minHeight: '270px !important', // eslint-disable-line @atlaskit/ui-styling-standard/no-important-styles
	},

	pressable: {
		backgroundColor: token('color.background.neutral.subtle'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		cursor: 'default',
		width: '100%',
	},
});

type AICollapseProps = {
	children: React.ReactNode;
	isCollapsed?: boolean;
	onExpandCollapse?: () => void;
	title: React.ReactNode;
	showReadMoreButton?: boolean;
	queryIntent?: QueryIntent;
	hideExpandCollapseButton?: boolean;
};

export const AICollapse = forwardRef<HTMLDivElement, AICollapseProps>(
	(
		{
			children,
			isCollapsed,
			onExpandCollapse,
			title,
			showReadMoreButton,
			queryIntent,
			hideExpandCollapseButton,
		},
		forwardedRef,
	) => {
		const containerRef = useRef<HTMLDivElement>(null);
		const { commonAttributes } = useAIAnswerContext();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
		const [fireAnalyticsEvent] = useAnalytics();
		const { formatMessage } = useIntl();

		const onExpand = () => {
			fireAnalyticsEvent(aiAnswerExpandClicked(commonAttributes));
			fireSAINUsageEvent({ metCriteria: SainAction.EXPAND_CLICKED, commonAttributes });
		};

		const onCollapse = () => fireAnalyticsEvent(aiAnswerCollapseClicked(commonAttributes));

		const handleExpandCollapse = () => {
			if (isCollapsed) {
				onExpand();
			} else {
				onCollapse();
			}

			onExpandCollapse?.();
		};

		const handleExpandCollapseButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			handleExpandCollapse?.();
		};

		const onContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
			const selection = window.getSelection()?.toString() ?? '';
			// Highlighting text should not expand the card
			const hasHighlightedText = selection.length > 0;
			// Clicking in a feedback modal or flag should not expand the card
			const isClickingOnContainer = containerRef.current?.contains(event.target as Node) ?? false;

			if (isCollapsed && !hasHighlightedText && isClickingOnContainer) {
				handleExpandCollapse?.();
			}
		};

		return (
			// This Box click handler is just a convenience for mouse users; there's a keyboard-accessible button that supports the same functionality
			<Box>
				<Box
					onClick={onContainerClick}
					ref={containerRef}
					xcss={cx(styles.pressable, isCollapsed && styles.pointer)}
					role="none"
				>
					<Inline alignBlock="center" spread="space-between" xcss={styles.header}>
						{title}
						{hideExpandCollapseButton ? null : (
							<IconButton
								appearance="subtle"
								icon={isCollapsed ? ChevronDownIcon : ChevronUpIcon}
								label={formatMessage(
									isCollapsed
										? messages.ai_answer_card_expand_button
										: messages.ai_answer_card_collapse_button,
								)}
								spacing="compact"
								onClick={handleExpandCollapseButtonClick}
							/>
						)}
					</Inline>
					<Box
						ref={forwardedRef}
						xcss={cx(
							styles.collapsableContainer,
							queryIntent === QueryIntent.EXPLICIT_QUESTION
								? isCollapsed
									? showReadMoreButton
										? styles.explicitQuestionCollapsedWithReadMore
										: styles.explicitQuestionCollapsed
									: styles.explicitQuestionExpanded
								: isCollapsed
									? showReadMoreButton
										? styles.rootCollapsedWithReadMore
										: styles.rootCollapsed
									: styles.rootExpanded,
						)}
						testId="ai-collapse-container"
					>
						{children}
					</Box>
				</Box>
				{showReadMoreButton && fg('kd_dynamic_layout_in_sain') && (
					<ShowMoreButton onClick={handleExpandCollapse} />
				)}
			</Box>
		);
	},
);
